import JSZip from 'jszip';
import type { Job, JobLog, JobWorkerAssignment } from './types';

export interface DownloadProgress {
  phase: 'preparing' | 'downloading' | 'packaging' | 'complete';
  currentFile?: string;
  filesDownloaded: number;
  totalFiles: number;
}

/**
 * Download all job data as a ZIP file
 *
 * Creates a ZIP archive with files in the root:
 * - metadata.json: Job details, assignments, and timestamps
 * - payload.json: Input parameters (if exists)
 * - result.json: Output data (if exists)
 * - error.json: Error information (if exists)
 * - logs.txt: Formatted execution logs
 * - download_errors.txt: List of files that failed to download (if any, only when includeFiles=true)
 * - input/: Directory with input files (input_file, input_lora_url if available, only when includeFiles=true)
 * - output/: Directory with output files (images, videos, etc. if available, only when includeFiles=true)
 *
 * Filename format: job-{uuid}-{timestamp}.zip
 *
 * @param job - The job object
 * @param logs - Array of job logs
 * @param assignments - Array of worker assignments
 * @param includeFiles - Whether to include binary files (input/output), defaults to true
 * @param onProgress - Optional callback for progress updates
 */
export async function downloadJobData(
  job: Job,
  logs: JobLog[],
  assignments: JobWorkerAssignment[],
  includeFiles: boolean = true,
  onProgress?: (progress: DownloadProgress) => void
): Promise<void> {
  try {
    const zip = new JSZip();
    const downloadErrors: string[] = [];

    // Count total files to download (only if including files)
    let totalFiles = 0;
    let filesDownloaded = 0;

    if (includeFiles) {
      // Count input files
      if (job.payload?.input_file && typeof job.payload.input_file === 'string') {
        totalFiles++;
      }
      if (job.payload?.input_lora_url && typeof job.payload.input_lora_url === 'string') {
        totalFiles++;
      }

      // Count output files
      if (job.result?.result?.output && Array.isArray(job.result.result.output)) {
        totalFiles += job.result.result.output.filter((item: unknown): item is string => typeof item === 'string').length;
      }
    }

    onProgress?.({ phase: 'preparing', filesDownloaded: 0, totalFiles });

    // Helper function to check if a string is a valid HTTP(S) URL
    const isHttpUrl = (str: string): boolean => {
      try {
        const url = new URL(str);
        return url.protocol === 'http:' || url.protocol === 'https:';
      } catch {
        return false;
      }
    };

    // Helper function to get file extension from MIME type
    const getExtensionFromMimeType = (mimeType: string): string => {
      const mimeToExt: Record<string, string> = {
        // Images
        'image/jpeg': '.jpg',
        'image/jpg': '.jpg',
        'image/png': '.png',
        'image/gif': '.gif',
        'image/webp': '.webp',
        'image/svg+xml': '.svg',
        'image/bmp': '.bmp',
        'image/tiff': '.tiff',
        // Videos
        'video/mp4': '.mp4',
        'video/webm': '.webm',
        'video/ogg': '.ogv',
        'video/quicktime': '.mov',
        'video/x-msvideo': '.avi',
        // Models/Data
        'application/x-safetensors': '.safetensors',
        // Archives
        'application/zip': '.zip',
        'application/x-tar': '.tar',
        'application/gzip': '.gz',
        // Documents
        'application/pdf': '.pdf',
        'application/json': '.json',
        'text/plain': '.txt',
      };

      const normalizedMime = mimeType.toLowerCase().split(';')[0].trim();
      return mimeToExt[normalizedMime] || '';
    };

    // Helper function to download file from URL with error tracking
    const downloadFile = async (url: string, description: string): Promise<{ blob: Blob; mimeType: string } | null> => {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 60000); // 60s timeout

        const response = await fetch(url, { signal: controller.signal });
        clearTimeout(timeoutId);

        if (!response.ok) {
          const errorMsg = `Failed to download ${description}: ${url} (HTTP ${response.status})`;
          console.warn(errorMsg);
          downloadErrors.push(errorMsg);
          return null;
        }
        const blob = await response.blob();
        const mimeType = response.headers.get('content-type') || blob.type || 'application/octet-stream';
        return { blob, mimeType };
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          const errorMsg = `Timeout downloading ${description}: ${url} (exceeded 60s)`;
          console.warn(errorMsg);
          downloadErrors.push(errorMsg);
          return null;
        }
        const errorMsg = `Error downloading ${description}: ${url} - ${error instanceof Error ? error.message : 'Unknown error'}`;
        console.warn(errorMsg);
        downloadErrors.push(errorMsg);
        return null;
      }
    };

    // Helper function to extract filename from URL and add proper extension
    const getFilenameFromUrl = (url: string, fallback: string, mimeType?: string): string => {
      try {
        const urlObj = new URL(url);
        const pathname = urlObj.pathname;
        let filename = pathname.substring(pathname.lastIndexOf('/') + 1);

        // Remove query parameters from filename
        filename = filename.split('?')[0];

        // Sanitize filename: remove dangerous characters
        filename = filename.replace(/[<>:"|?*\0]/g, '_').replace(/\.\./g, '_');
        if (!filename || filename === '.' || filename === '_') {
          filename = fallback;
        }

        // Add or fix extension based on MIME type if provided
        if (mimeType) {
          const properExtension = getExtensionFromMimeType(mimeType);
          if (properExtension) {
            // Check if filename already has an extension
            const lastDot = filename.lastIndexOf('.');
            if (lastDot === -1) {
              // No extension, add it
              filename += properExtension;
            } else {
              // Has extension, check if it matches the MIME type
              const currentExt = filename.substring(lastDot).toLowerCase();
              if (currentExt !== properExtension) {
                // Extension doesn't match MIME type, replace it
                filename = filename.substring(0, lastDot) + properExtension;
              }
            }
          }
        }

        return filename;
      } catch {
        return fallback;
      }
    };

    // 1. Create metadata.json with job details and assignments
    const metadata = {
      job_id: job.id,
      type: job.type_name,
      status: job.status_name,
      priority: job.priority,
      retry_count: job.retry_count,
      max_retries: job.max_retries,
      timestamps: {
        created: job.create_ts,
        claimed: job.claim_ts,
        started: job.start_ts,
        completed: job.complete_ts,
        updated: job.update_ts,
      },
      callback_url: job.callback_url,
      api_key_id: job.api_key_id,
      assignments: assignments.map((a) => ({
        worker_id: a.worker_id,
        claimed_at: a.create_ts,
        heartbeat_at: a.heartbeat_ts,
        released_at: a.release_ts,
      })),
    };
    zip.file('metadata.json', JSON.stringify(metadata, null, 2));

    // 2. Create payload.json if exists
    if (job.payload) {
      zip.file('payload.json', JSON.stringify(job.payload, null, 2));
    }

    // 3. Create result.json if exists
    if (job.result) {
      zip.file('result.json', JSON.stringify(job.result, null, 2));
    }

    // 4. Create error.json if exists
    if (job.error) {
      zip.file('error.json', JSON.stringify(job.error, null, 2));
    }

    // 5. Create logs.txt with formatted logs
    if (logs && logs.length > 0) {
      const logsText = logs
        .map((log) => {
          const timestamp = log.log_ts || 'N/A';
          const level = log.level.toUpperCase().padEnd(7);
          const message = log.message;
          return `[${timestamp}] [${level}] ${message}`;
        })
        .join('\n');
      zip.file('logs.txt', logsText);
    } else {
      zip.file('logs.txt', 'No logs available\n');
    }

    // 6. Download input files if available in payload (only if includeFiles is true)
    if (includeFiles && job.payload) {
      // Download main input file
      if (job.payload.input_file) {
        const inputFile = job.payload.input_file;
        if (typeof inputFile === 'string' && isHttpUrl(inputFile)) {
          onProgress?.({ phase: 'downloading', currentFile: 'input file', filesDownloaded, totalFiles });
          const result = await downloadFile(inputFile, 'input file');
          if (result) {
            const filename = getFilenameFromUrl(inputFile, 'input_file', result.mimeType);
            zip.file(`input/${filename}`, result.blob);
          }
          filesDownloaded++;
        }
      }

      // Download input LoRA file
      if (job.payload.input_lora_url) {
        const loraUrl = job.payload.input_lora_url;
        if (typeof loraUrl === 'string' && isHttpUrl(loraUrl)) {
          onProgress?.({ phase: 'downloading', currentFile: 'LoRA file', filesDownloaded, totalFiles });
          const result = await downloadFile(loraUrl, 'LoRA file');
          if (result) {
            const filename = getFilenameFromUrl(loraUrl, 'lora.safetensors', result.mimeType);
            zip.file(`input/${filename}`, result.blob);
          }
          filesDownloaded++;
        }
      }
    }

    // 7. Download output files if available in result (only if includeFiles is true)
    // Worker sends: { "result": { "output": [...] }, "execution_time": ... }
    // So we need to check job.result.result.output
    if (includeFiles && job.result && job.result.result && job.result.result.output && Array.isArray(job.result.result.output)) {
      const outputUrls = job.result.result.output.filter(
        (item: unknown): item is string => typeof item === 'string' && isHttpUrl(item)
      );

      // Download output files with max 4 concurrent downloads
      const maxConcurrent = 4;
      const downloadTasks = outputUrls.map((url: string, i: number) => async () => {
        onProgress?.({
          phase: 'downloading',
          currentFile: `output file ${i + 1}/${outputUrls.length}`,
          filesDownloaded,
          totalFiles
        });
        const result = await downloadFile(url, `output file ${i + 1}`);
        if (result) {
          const filename = getFilenameFromUrl(url, `output_${i + 1}`, result.mimeType);
          zip.file(`output/${filename}`, result.blob);
        }
        filesDownloaded++;
      });

      // Process downloads with concurrency limit
      const executing: Promise<void>[] = [];
      for (const task of downloadTasks) {
        const promise = task().then(() => {
          executing.splice(executing.indexOf(promise), 1);
        });
        executing.push(promise);

        if (executing.length >= maxConcurrent) {
          await Promise.race(executing);
        }
      }

      // Wait for remaining downloads to complete
      await Promise.all(executing);
    }

    // 8. Create download_errors.txt if there were any errors (only when including files)
    if (includeFiles && downloadErrors.length > 0) {
      const errorsText = `Download Errors (${downloadErrors.length} total)\n` +
        `${'='.repeat(60)}\n\n` +
        downloadErrors.map((err, i) => `${i + 1}. ${err}`).join('\n\n');
      zip.file('download_errors.txt', errorsText);
    }

    // Generate ZIP file
    onProgress?.({ phase: 'packaging', filesDownloaded: totalFiles, totalFiles });
    const blob = await zip.generateAsync({ type: 'blob' });

    // Create download filename with timestamp
    const now = new Date();
    const timestamp = now
      .toISOString()
      .replace(/[-:]/g, '')
      .replace(/\.\d{3}Z$/, '')
      .replace('T', '-');
    const filename = `job-${job.id}-${timestamp}.zip`;

    // Trigger download
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Failed to create job data download:', error);
    throw new Error(
      `Failed to create download: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
}
